import React, { useState, useContext, useImperativeHandle, forwardRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Box,
  Chip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { ErpContext } from "../../context/ErpContext";

const TablaLoteUbicaciones = forwardRef(({
  loteId,
  itemId,
  ubicaciones,
  stockPorUbicacion,
  onStockActualizado,
}, ref) => {
  const { updateLoteItemUbicacionStock } = useContext(ErpContext);

  const [editingStock, setEditingStock] = useState({});
  const [savingUbicacion, setSavingUbicacion] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getStockActual = (ubicacionId) => {
    const stock = stockPorUbicacion.find(
      (s) => s.idUbicacion === ubicacionId
    );
    if (!stock || stock.stock === undefined || stock.stock === null) {
      return 0;
    }
    // Garantizar que sea número (protección contra strings, decimales de BD, etc.)
    const valorNumerico = parseFloat(stock.stock);
    return !isNaN(valorNumerico) ? valorNumerico : 0;
  };

  const handleStockChange = (ubicacionId, value) => {
    setEditingStock({
      ...editingStock,
      [ubicacionId]: value,
    });
  };

  const handleSaveStock = async (ubicacionId) => {
    const newStock = editingStock[ubicacionId];

    // Validar que sea un número válido
    if (newStock === undefined || newStock === "" || isNaN(newStock) || parseFloat(newStock) < 0) {
      setError("Ingresa un número válido mayor o igual a 0");
      return;
    }

    setSavingUbicacion(ubicacionId);
    setError(null);
    setSuccess(null);

    try {
      await updateLoteItemUbicacionStock({
        idLote: loteId,
        idItem: itemId,
        idUbicacion: ubicacionId,
        stock: parseFloat(newStock),
      });

      setSuccess(`Stock actualizado en ${ubicaciones.find(u => u.id === ubicacionId)?.descripcion}`);
      
      // Limpiar el estado de edición
      const newEditingStock = { ...editingStock };
      delete newEditingStock[ubicacionId];
      setEditingStock(newEditingStock);
      
      // Notificar al componente padre para recargar datos
      onStockActualizado();

      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error("Error al actualizar stock:", err);
      setError(
        err.response?.data?.message || 
        "Error al actualizar el stock"
      );
    } finally {
      setSavingUbicacion(null);
    }
  };

  const hasChanges = (ubicacionId) => {
    if (editingStock[ubicacionId] === undefined) return false;
    return parseFloat(editingStock[ubicacionId]) !== getStockActual(ubicacionId);
  };

  const getTotalStock = () => {
    return stockPorUbicacion.reduce((total, s) => total + (s.stock || 0), 0);
  };

  // 🆕 Función para guardar todos los cambios pendientes (expuesta al padre)
  const guardarTodosLosCambiosPendientes = async () => {
    const cambiosPendientes = Object.entries(editingStock).filter(([ubicacionId, newStock]) => {
      // Solo incluir los que realmente cambiaron y son válidos
      if (newStock === undefined || newStock === "" || isNaN(newStock) || parseFloat(newStock) < 0) {
        return false;
      }
      return parseFloat(newStock) !== getStockActual(parseInt(ubicacionId));
    });

    if (cambiosPendientes.length === 0) {
      return { exitosos: 0, fallidos: 0, errores: [] };
    }

    console.log(`💾 Guardando ${cambiosPendientes.length} cambios pendientes de stock de lotes...`);

    const resultados = {
      exitosos: 0,
      fallidos: 0,
      errores: []
    };

    // Guardar todos los cambios en paralelo
    const promesas = cambiosPendientes.map(async ([ubicacionId, newStock]) => {
      try {
        await updateLoteItemUbicacionStock({
          idLote: loteId,
          idItem: itemId,
          idUbicacion: parseInt(ubicacionId),
          stock: parseFloat(newStock),
        });
        resultados.exitosos++;
        return { success: true, ubicacionId };
      } catch (err) {
        resultados.fallidos++;
        const ubicacionNombre = ubicaciones.find(u => u.id === parseInt(ubicacionId))?.descripcion || `ID ${ubicacionId}`;
        const errorMsg = `${ubicacionNombre}: ${err.response?.data?.message || err.message}`;
        resultados.errores.push(errorMsg);
        console.error(`Error al guardar stock de lote en ${ubicacionNombre}:`, err);
        return { success: false, ubicacionId, error: err };
      }
    });

    await Promise.all(promesas);

    // Limpiar todos los cambios guardados exitosamente
    if (resultados.exitosos > 0) {
      setEditingStock({});
      onStockActualizado();
    }

    console.log(`✅ Guardado de lotes: ${resultados.exitosos} exitosos, ${resultados.fallidos} fallidos`);
    
    return resultados;
  };

  // Exponer funciones al componente padre mediante ref
  useImperativeHandle(ref, () => ({
    guardarTodosLosCambiosPendientes
  }));

  return (
    <Card elevation={2} sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: "flex", alignItems: "center" }}>
            <WarehouseIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
            Stock por Ubicación
          </Typography>
          <Chip
            label={`Total: ${getTotalStock()} unidades`}
            color="primary"
            sx={{
              backgroundColor: "rgb(244, 147, 14)",
              color: "white",
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: 600 }}>Ubicación</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Stock Actual
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Nuevo Stock
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ubicaciones.map((ubicacion) => {
                const stockActual = getStockActual(ubicacion.id);
                const stockEditado = editingStock[ubicacion.id];
                const isSaving = savingUbicacion === ubicacion.id;
                const showSaveButton = hasChanges(ubicacion.id);

                return (
                  <TableRow key={ubicacion.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {ubicacion.descripcion}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: stockActual === 0 ? "gray" : "black" }}>
                        {stockActual.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        value={
                          stockEditado !== undefined
                            ? stockEditado
                            : stockActual
                        }
                        onChange={(e) =>
                          handleStockChange(ubicacion.id, e.target.value)
                        }
                        disabled={isSaving}
                        inputProps={{
                          min: 0,
                          step: "0.01",
                          style: { textAlign: "right" },
                        }}
                        sx={{
                          maxWidth: 120,
                          "& .MuiInputBase-input": {
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {showSaveButton && (
                        <Tooltip title="Guardar cambios">
                          <IconButton
                            size="small"
                            onClick={() => handleSaveStock(ubicacion.id)}
                            disabled={isSaving}
                            sx={{
                              color: "rgb(244, 147, 14)",
                              "&:hover": {
                                backgroundColor: "rgba(244, 147, 14, 0.1)",
                              },
                            }}
                          >
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            💡 <strong>Tip:</strong> Modifica el stock en el campo "Nuevo Stock" y haz clic en el ícono de guardar para actualizar.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
});

export default TablaLoteUbicaciones;



